import React from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormClearErrors,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import { Button, DatepickerRange, Select, SpinnerOverlay } from '@components/common';

import { useQueryPortalMilestonesAssignees } from '@hooks/portal/milestones/useQueryPortalMilestonesAssignees';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { fieldsToTestDetails, milestoneFormFields, tabsNames } from '../constants';
import { datePickerErrorText } from './constants';

import styles from './Details.module.scss';

interface IDetailsProps {
	isEditMode: boolean;
	isViewMode: boolean;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	clearErrors: UseFormClearErrors<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	isDetailsTabError: boolean;
	setCurrentTab: (value: string) => void;
	projectId?: string;
	control: Control;
}

const Details = ({
	errors,
	watch,
	setValue,
	clearErrors,
	isDetailsTabError,
	trigger,
	setCurrentTab,
	isEditMode,
	isViewMode,
	projectId,
	control,
}: IDetailsProps) => {
	const selectedProjectId = (isEditMode || isViewMode) ? projectId : watch(milestoneFormFields.project)?.value;

	const {
		portalMilestonesAssignees,
		isLoadingPortalMilestonesAssignees,
	} = useQueryPortalMilestonesAssignees(selectedProjectId);

	const setStartDate = (date: number) => {
		if (date) clearErrors(milestoneFormFields.startDate);

		setValue(milestoneFormFields.startDate, date, { shouldDirty: true });
	};

	const setEndDate = (date: number) => {
		if (date) clearErrors(milestoneFormFields.endDate);

		setValue(milestoneFormFields.endDate, date, { shouldDirty: true });
	};

	const onChangeAssignees = (options: TSelectOption<string>[]) => setValue(milestoneFormFields.assignees, options);

	const assigneesValue = watch(milestoneFormFields.assignees);

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestDetails);

		if (!isValid) return;

		setCurrentTab(tabsNames.dependencies);
	};

	const onClickBackHandler = () => {
		setCurrentTab(tabsNames.basicInfo);
	};

	return (
		<>
			<div className={styles.detailsContainer}>
				{isLoadingPortalMilestonesAssignees && <SpinnerOverlay />}
				<div className={styles.datePickersContainer}>
					<DatepickerRange
						disableInput
						disabled={isViewMode}
						errorFromDate={errors?.startDate?.message as string}
						errorMessage={datePickerErrorText}
						errorToDate={errors?.endDate?.message as string}
						fromLabel='Start date'
						handleChangeFromDate={setStartDate}
						handleChangeToDate={setEndDate}
						isMobileDevice={false}
						requiredFromDate
						requiredToDate
						toLabel='End date'
						value={{
							from: watch(milestoneFormFields.startDate) as number,
							to: watch(milestoneFormFields.endDate) as number,
						}}
					/>
				</div>
				<div className={styles.assignees}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						disabled={isViewMode}
						handleChange={onChangeAssignees}
						isError={!!errors.assignees}
						isMulti
						label='Assignees'
						multiselectText='assignees selected'
						name={milestoneFormFields.assignees}
						options={portalMilestonesAssignees}
						placeholder='Select assignees'
						value={assigneesValue}
					/>
				</div>
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Button
						disabled={isDetailsTabError}
						onClick={onSubmit}
						text='Next'
					/>
				</div>
			}
		</>
	);
};

export default Details;
